<template>
  <div class="totp-auth-verifier">
    <div class="w-full flex flex-col gap-2">
      <div
        class="h-16 flex items-center justify-between gap-2 border border-rim-300 px-4 py-[10px] rounded-lg hover:rim-400"
      >
        <div class="flex items-center gap-2">
          <SvgIcon :name="iconName" :size="26" />
          <div class="flex flex-col justify-between">
            <div class="ink-14 font-medium text-ink-950">
              {{ titleText }}
            </div>
            <div class="ink-12 text-ink-700">
              {{ descText }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <slot name="actions"></slot>
        </div>
      </div>

      <div class="w-full">
        <VerifyCodeInput
          :length="6"
          inputmode="numeric"
          autocomplete="one-time-code"
          @submit="handleSubmit"
        />
        <div class="ink-12 text-ink-600 mt-2">
          {{ hintText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import i18n from '@/locales'
import { VerifyCodeInput, SvgIcon } from '@/components'
import { computed, ref } from 'vue'
import type { Condition } from '../types'
import { useVerifierRunner } from '../useVerifierRunner'

const t = (i18n.global as any).t

const props = defineProps<{
  provider: string
  condition: Condition
  passageKey: string
  securityCode: string
  resolvedInstanceId: string
  make: (c: Condition) => Promise<any>
  verifyData: { email: string; phone: string; loginType: number; unauthorized: boolean }
}>()

const emit = defineEmits<{ (e: 'verified', ok: boolean): void }>()

const { verify } = useVerifierRunner(props.condition, props.make, props.verifyData.unauthorized)
const verifyResult = ref<boolean | undefined>(undefined)

const iconName = computed(() => {
  // const p = props.provider?.toLowerCase()
  // if (p === 'google') return 'brand-google'
  // if (p === 'microsoft') return 'brand-microsoft'
  return 'account-app-verifier'
})

const titleText = computed(() => {
  const p = props.provider?.toLowerCase()
  const map: Record<string, string> = {
    google: t('components.authenticator.google.title', 'Google 验证器'),
    microsoft: t('components.authenticator.microsoft.title', 'Microsoft 验证器'),
  }
  return map[p] ?? t('components.authenticator.generic.title', '验证器 App')
})

const descText = computed(() => {
  const p = props.provider?.toLowerCase()
  const map: Record<string, string> = {
    google: t(
      'components.authenticator.google.desc',
      '请打开 Google 验证器 App，输入 6 位动态码（每 30 秒刷新）。',
    ),
    microsoft: t(
      'components.authenticator.microsoft.desc',
      '请打开 Microsoft Authenticator，输入 6 位动态码（每 30 秒刷新）。',
    ),
  }
  return (
    map[p] ??
    t(
      'components.authenticator.generic.desc',
      '请打开验证器 App，输入 6 位动态码（每 30 秒刷新）。',
    )
  )
})

const hintText = computed(() => {
  return verifyResult.value === true
    ? t('components.authenticator.hint.pass', '验证成功')
    : t(
        'components.authenticator.hint.tip',
        '若验证码反复失败，请检查手机系统时间是否自动同步，或改用其它方式。',
      )
})

async function handleSubmit(code: string) {
  const ok = await verify({ code })
  verifyResult.value = ok
  emit('verified', !!ok)
}
</script>
